import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  getSuremedRequestFormsApiV1ProvidersSuremedRequestFormsGet,
  listDocumentRequestsApiV1CasesCaseIdDocumentRequestsGet,
} from "@/api/generated/sdk.gen";
import type {
  ProviderDocumentRequestSchema,
  RequestFormsCatalogueSchema,
} from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/**
 * The SureMed "what to request" catalogue (request type -> named groups ->
 * selectable items). POC: backend serves it from config; swaps to live
 * request-forms discovery later with no client change. Long staleTime — it's
 * reference data that rarely changes within a session.
 */
export const useRequestForms = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.REQUEST_FORMS, "suremed"],
    queryFn: async (): Promise<RequestFormsCatalogueSchema> => {
      const response =
        await getSuremedRequestFormsApiV1ProvidersSuremedRequestFormsGet({
          client: apiClient,
        });
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch request forms: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      if (!response.data) {
        throw new Error("Request forms not found");
      }
      return response.data;
    },
    enabled,
    staleTime: 30 * 60 * 1000,
  });
};

/**
 * The document requests raised on a case. Drives the only-once rule: if this
 * returns a non-empty list, the "request documents" form is hidden and the
 * existing request's status is shown instead.
 */
export const useCaseDocumentRequests = ({
  caseId,
  enabled = true,
}: {
  caseId: string | undefined;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.DOCUMENT_REQUESTS, caseId],
    queryFn: async (): Promise<ProviderDocumentRequestSchema[]> => {
      const response = await listDocumentRequestsApiV1CasesCaseIdDocumentRequestsGet(
        {
          client: apiClient,
          path: { case_id: caseId as string },
        }
      );
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch document requests: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      return response.data ?? [];
    },
    enabled: enabled && !!caseId,
  });
};
